import forOwn from 'mout/object/forOwn'
import values from 'mout/object/values'
import sortByPropText from '../content/manageSets/sortByPropText';

// Calculates clocks and star rating for Time Management
export default function getTimeManagementRating(userAssessment) {
  const ratingThresholds = {
    'English': [
      {percent: 5, rating: 10},
      {percent: 10, rating: 9},
      {percent: 20, rating: 8},
      {percent: 25, rating: 7},
      {percent: 30, rating: 6},
      {percent: 40, rating: 5},
      {percent: 50, rating: 4},
      {percent: 60, rating: 3},
      {percent: 70, rating: 2},
      {percent: 80, rating: 1},
    ],
    'Math': [
      {percent: 150, rating: 10},
      {percent: 125, rating: 9},
      {percent: 100, rating: 8},
      {percent: 90, rating: 7},
      {percent: 80, rating: 6},
      {percent: 70, rating: 5},
      {percent: 60, rating: 4},
      {percent: 50, rating: 3},
      {percent: 35, rating: 2},
      {percent: 30, rating: 1},
    ],
    'Reading': [
      {percent: 5, rating: 10},
      {percent: 10, rating: 9},
      {percent: 20, rating: 8},
      {percent: 25, rating: 7},
      {percent: 30, rating: 6},
      {percent: 40, rating: 5},
      {percent: 50, rating: 4},
      {percent: 60, rating: 3},
      {percent: 70, rating: 2},
      {percent: 80, rating: 1},
    ],
    'Science': [
      {percent: 10, rating: 10},
      {percent: 20, rating: 9},
      {percent: 30, rating: 8},
      {percent: 35, rating: 7},
      {percent: 40, rating: 6},
      {percent: 50, rating: 5},
      {percent: 60, rating: 4},
      {percent: 70, rating: 3},
      {percent: 80, rating: 2},
      {percent: 90, rating: 1},
    ]
  }

  const subjects = ['English', 'Reading', 'Science']

  function getSubtest(subject, userAssessment) {
    let subtest
    forOwn(userAssessment.assessmentSubtests, (sub, key) => {
      if (sub.displayName === subject) {
        subtest = sub
      }
    })
    return subtest
  }

  function findItem (byItem, itemNumber) {
    let item
    forOwn(byItem, it => {
      if (parseInt(it.index, 10) + 1 == itemNumber) {
        item = it
      }
    })
    return item
  }

  function calculatePercentage(items, byItem) {
    let totalRaw = 0, totalPossible = 0
    items.forEach(itemNumber => {
      const item = findItem(byItem, itemNumber)
      totalRaw += item && parseInt(item.raw, 10) || 0
      totalPossible += item && parseInt(item.possible, 10) || 0
    })
    return totalRaw / totalPossible
  }

  function getAveragePercentages(subject, userAssessment, skillProp) {
    const subtest = getSubtest(subject, userAssessment)
    const subtestId = subtest.setId
    const byItem = userAssessment.scoring.byItem[subtestId]
    const items = subtest[skillProp] ? subtest[skillProp] : []
    return calculatePercentage(items, byItem)
  }

  function getMathPercentages(userAssessment, skillProp) {
    const subtest = getSubtest('Math', userAssessment)
    const subtestId = subtest.setId
    const byItem = userAssessment.scoring.byItem[subtestId]
    const items = sortByPropText(values(byItem), 'index').slice(30)
    const sortedItems = items.sort(function sortByPercentCorrect (a, b) {
      if (a.raw / a.possible < b.raw / b.possible ) {
        return -1
      } else if (a.raw / a.possible > b.raw / b.possible) {
        return 1
      } else {
        return 0
      }
    })
    if (skillProp === 'timeManagementHard') {
      return calculatePercentage(sortedItems.slice(0, 7).map((it)=>it.index + 1), byItem)
    } else if (skillProp === 'timeManagementEasy') {
      return calculatePercentage(sortedItems.slice(-7).map((it)=>it.index + 1), byItem)
    } else {
      return null
    }
  }

  function ratingTest(r, score, subject) {
    if (subject === 'Math') {
      return score > r.percent
    } else {
      return score < r.percent
    }
  }

  function checkRating(ratingThresholds, subject, score) {
    const ratings = ratingThresholds[subject]
    let rating = 1, ratingFound = false
    ratings.forEach(function seeIfRatingApplies(r) {
      if (!ratingFound && ratingTest(r, score, subject)) {
        ratingFound = true
        rating = r.rating
      }
    })
    return rating
  }

  function calculateSubjectRating(subject, userAssessment, ratingThresholds) {
    const easy = getAveragePercentages(subject, userAssessment, 'timeManagementEasy')
    const hard = getAveragePercentages(subject, userAssessment, 'timeManagementHard')
    let score
    if (hard !== 0) {
      score = Math.round((easy / hard - 1) * 100)
    } else {
      score = 100
    }
    return checkRating(ratingThresholds, subject, score)
  }

  function calculateMathSubjectRating(userAssessment, ratingThresholds) {
    const easy = getMathPercentages(userAssessment, 'timeManagementEasy')
    const hard = getMathPercentages(userAssessment, 'timeManagementHard')
    let score
    if (hard !== 0) {
      score = Math.round((easy / hard - 1) * 100)
    } else { 
      score = 1
    }
    return checkRating(ratingThresholds, 'Math', score)
  }

  function calculateCompositeRating(clocks) {
    let raw = 0
    forOwn(clocks, function totalScore(score) {
      raw += parseInt(score, 10)
    })
    return Math.round(raw / 4)
  }

  function calculateStars(composite) {
    return Math.round(composite / 2)
  }

  function computeTimeManagement(subjects, userAssessment, ratingThresholds) {
    let timeManagement = {
      clocks: {},
    }
    subjects.forEach(function getTimeManagement(subject) {
      timeManagement.clocks[subject] = calculateSubjectRating(subject, userAssessment, ratingThresholds)
    })
    timeManagement.clocks['Math'] = calculateMathSubjectRating(userAssessment, ratingThresholds)
    timeManagement.clocks['Composite'] = calculateCompositeRating(timeManagement.clocks)
    timeManagement.stars = calculateStars(timeManagement.clocks['Composite'])
    return timeManagement
  }

  return computeTimeManagement(subjects, userAssessment, ratingThresholds)

}


// WEBPACK FOOTER //
// ./src/helpers/assessment/getTimeManagementRating.js